import React, { useContext } from 'react'
import { ActivityIndicator, ScrollView, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { ImageStyle } from 'react-native-fast-image'
import { LinkingContext, useNavigation } from '@react-navigation/native'
import { StackNavigationProp } from '@react-navigation/stack'
import { useTheme } from '@ui-kitten/components'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Button, Icon, Text, UI_ICONS_MAPPING } from '@components/base'
import { NavigationBar, OutlinedIcon } from '@src/components'
import { openUrl } from '@src/utils'
import { AppStackParamList } from '@src/navigation/types'
import { MarkdownView } from '@src/components/Markdown'
import { getDeepLinkRoute } from '@src/utils/openUrl'
import { ImageWithPlaceholder } from '@src/components/Image/FastImageWithPlaceholder'
import { UI_COLORS_MAPPING } from '@src/style/uiColors'
import { Link, UiColor, UiIcon } from '@src/types.ts'

export interface CompletedComponentProps {
  nextScreen?: string | undefined
  nextScreenParams?: any
  onCloseButtonPress: () => void
  title?: string | null
  header?: string
  introMarkdown?: string | null
  icon?: UiIcon | null
  iconColor?: UiColor | null
  showCoachPhoto?: boolean
  primaryActionText?: string | null
  secondaryActionLink?: Link | null
  footnote?: string | null
  coachPhotoUri?: string | null
}

export const CompletedComponent = (props: CompletedComponentProps) => {
  const styles = useStyleSheet(themedStyles)
  const insets = useSafeAreaInsets()
  const navigation = useNavigation<StackNavigationProp<AppStackParamList>>()
  const linking = useContext(LinkingContext)
  const theme = useTheme()

  const {
    nextScreen,
    nextScreenParams,
    onCloseButtonPress,
    title,
    header,
    introMarkdown,
    icon,
    iconColor,
    showCoachPhoto,
    primaryActionText,
    footnote,
    secondaryActionLink,
    coachPhotoUri,
  } = props

  const onPrimaryButtonPress = () => {
    if (nextScreen) {
      navigation.replace(nextScreen as any, nextScreenParams)
      return
    }

    onCloseButtonPress()
  }

  const onLinkPress = () => {
    if (!secondaryActionLink) {
      return
    }

    if (secondaryActionLink.deepLink) {
      const deepLinkRoute = getDeepLinkRoute(secondaryActionLink.deepLink, linking.options?.config)
      navigation.replace(deepLinkRoute?.name as any, deepLinkRoute?.params)
      return
    }

    if (secondaryActionLink.url) {
      openUrl(secondaryActionLink.url)
    }
  }

  const showNavigationBar = !!title
  const iconName = icon && UI_ICONS_MAPPING[icon]
  const iconThemeColor = iconColor && UI_COLORS_MAPPING[iconColor]

  const renderIcon = () => {
    if (!iconName || showCoachPhoto) {
      return null
    }

    if (iconThemeColor) {
      return (
        <Icon
          size={56}
          weight="fill"
          name={iconName}
          color={theme[iconThemeColor]}
          style={styles.icon}
        />
      )
    }

    return <OutlinedIcon iconName={iconName} iconStyle={styles.outlinedIcon} />
  }

  return (
    <View style={styles.topLevelContainer}>
      {showNavigationBar && (
        <NavigationBar
          title={title}
          style={styles.navigationBar}
          backgroundColor={styles.topLevelContainer.backgroundColor}
        />
      )}
      <ScrollView
        bounces={false}
        contentContainerStyle={[
          styles.container,
          !showNavigationBar && { paddingTop: 60 + insets.top },
          { paddingBottom: insets.bottom },
        ]}
      >
        <View style={styles.headContainer}>
          {renderIcon()}
          {showCoachPhoto && !iconName && coachPhotoUri && (
            <ImageWithPlaceholder
              style={styles.nutritionistPhoto as ImageStyle}
              source={{ uri: coachPhotoUri }}
              placeholderComponent={
                <ActivityIndicator animating size="small" {...styles.spinner} />
              }
            />
          )}
          <Text type="title-3" style={styles.title}>
            {header}
          </Text>
          {!!introMarkdown && (
            <MarkdownView content={introMarkdown} paragraphStyle={styles.description} />
          )}
        </View>
        <View>
          {!!footnote && (
            <Text type="tiny" style={styles.footnote}>
              {footnote}
            </Text>
          )}
          <Button
            type="primary"
            size="block"
            onPress={onPrimaryButtonPress}
            accessibilityLabel={primaryActionText ?? 'Close questionnaire'}
            style={styles.button}
          >
            {primaryActionText}
          </Button>
          {secondaryActionLink && (
            <Button
              type="transparent"
              size="block"
              onPress={onLinkPress}
              accessibilityLabel={secondaryActionLink.text}
              style={styles.button}
            >
              {secondaryActionLink.text}
            </Button>
          )}
        </View>
      </ScrollView>
    </View>
  )
}

const themedStyles = StyleService.create({
  topLevelContainer: {
    flex: 1,
    backgroundColor: 'theme.background',
  },
  container: {
    paddingHorizontal: 16,
    flexGrow: 1,
    justifyContent: 'space-between',
  },
  nutritionistPhoto: {
    width: 80,
    height: 80,
    borderRadius: 48,
    marginVertical: 24,
  },
  spinner: {
    color: 'theme.surface.base',
  },
  headContainer: {
    alignItems: 'center',
  },
  navigationBar: {
    marginBottom: 32,
    alignSelf: 'stretch',
  },
  icon: {
    marginVertical: 24,
  },
  outlinedIcon: {
    color: 'theme.info.base',
  },
  title: {
    marginBottom: 16,
    textAlign: 'center',
    maxWidth: 280,
  },
  description: {
    color: 'theme.text.primary',
    textAlign: 'center',
    marginBottom: 32,
  },
  footnote: {
    marginTop: 8,
    marginBottom: 8,
    textAlign: 'center',
  },
  button: {
    marginBottom: 8,
  },
})
